import React from 'react';
import { Trophy } from 'lucide-react';
import { Painel, Vazio, fmtBRL } from './DataCenterKpis';

const Th = ({ children, className = '' }) => <th className={`px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-purple-300/80 text-left whitespace-nowrap ${className}`}>{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-2 py-1.5 text-xs text-gray-700 dark:text-gray-200 ${className}`}>{children}</td>;

const BarraMeta = ({ pct }) => {
  if (pct == null) return <span className="text-[10px] text-gray-400">sem meta</span>;
  const cor = pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-purple-950/60 overflow-hidden"><div className={`h-full rounded-full ${cor}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      <span className="text-[10px] tabular-nums w-10 text-right">{pct}%</span>
    </div>
  );
};

const Medalha = ({ pos }) => {
  const cores = ['text-yellow-400', 'text-gray-300', 'text-amber-600'];
  return pos < 3 ? <Trophy className={`w-3.5 h-3.5 ${cores[pos]}`} /> : <span className="text-[10px] text-gray-400 tabular-nums">{pos + 1}º</span>;
};

export const TabelaVendedores = ({ itens, mesMeta }) => (
  <Painel titulo="Produção por vendedor" descricao={`Ranking por instaladas • meta do mês ${mesMeta || ''}`} className="xl:col-span-2" testid="dc-vendedores">
    {!itens?.length ? <Vazio /> : (
      <div className="overflow-x-auto -mx-2">
        <table className="w-full">
          <thead><tr className="border-b border-gray-100 dark:border-purple-900/40"><Th>#</Th><Th>Vendedor</Th><Th>Equipe</Th><Th className="text-right">Vendas</Th><Th className="text-right">Instaladas</Th><Th className="text-right">Conv.</Th><Th className="text-right">Cancel.</Th><Th className="text-right">Devolv.</Th><Th className="text-right">Valor inst.</Th><Th>Meta</Th><Th className="text-right">LNP BQ</Th></tr></thead>
          <tbody>
            {itens.map((v, i) => (
              <tr key={v.vendedor_id} className="border-b border-gray-50 dark:border-purple-900/20 hover:bg-gray-50 dark:hover:bg-purple-900/10" data-testid={`dc-vendedor-${i}`}>
                <Td><Medalha pos={i} /></Td>
                <Td className="font-semibold whitespace-nowrap">{v.vendedor_nome}</Td>
                <Td className="text-gray-500">{v.equipe}</Td>
                <Td className="text-right tabular-nums">{v.total}</Td>
                <Td className="text-right tabular-nums font-bold text-emerald-600 dark:text-emerald-400">{v.instaladas}</Td>
                <Td className="text-right tabular-nums">{v.conversao}%</Td>
                <Td className="text-right tabular-nums text-red-500">{v.canceladas}</Td>
                <Td className="text-right tabular-nums text-orange-500">{v.devolvidas}</Td>
                <Td className="text-right tabular-nums whitespace-nowrap">{fmtBRL(v.valor_instalado)}</Td>
                <Td><BarraMeta pct={v.pct_meta} />{v.meta ? <span className="text-[10px] text-gray-400">{v.instaladas}/{v.meta}</span> : null}</Td>
                <Td className="text-right tabular-nums">{v.lnp_pct_bloqueadas != null ? <span className={v.lnp_pct_bloqueadas > 0 ? 'text-red-500 font-semibold' : 'text-gray-400'}>{v.lnp_pct_bloqueadas}%</span> : <span className="text-gray-300">—</span>}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Painel>
);

export const TabelaEquipes = ({ itens }) => (
  <Painel titulo="Produção por equipe" descricao="Soma dos vendedores de cada equipe" testid="dc-equipes">
    {!itens?.length ? <Vazio /> : (
      <table className="w-full">
        <thead><tr className="border-b border-gray-100 dark:border-purple-900/40"><Th>Equipe</Th><Th className="text-right">Vend.</Th><Th className="text-right">Vendas</Th><Th className="text-right">Inst.</Th><Th className="text-right">Conv.</Th><Th>Meta</Th></tr></thead>
        <tbody>
          {itens.map((e) => (
            <tr key={e.equipe} className="border-b border-gray-50 dark:border-purple-900/20">
              <Td className="font-semibold">{e.equipe}</Td>
              <Td className="text-right tabular-nums text-gray-500">{e.vendedores}</Td>
              <Td className="text-right tabular-nums">{e.total}</Td>
              <Td className="text-right tabular-nums font-bold text-emerald-600 dark:text-emerald-400">{e.instaladas}</Td>
              <Td className="text-right tabular-nums">{e.conversao}%</Td>
              <Td><BarraMeta pct={e.pct_meta} /></Td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Painel>
);

export const TabelaProdutos = ({ itens }) => (
  <Painel titulo="Produtos e valores" descricao="Planos mais vendidos no período" testid="dc-produtos">
    {!itens?.length ? <Vazio /> : (
      <table className="w-full">
        <thead><tr className="border-b border-gray-100 dark:border-purple-900/40"><Th>Categoria</Th><Th>Plano</Th><Th className="text-right">Vendas</Th><Th className="text-right">Inst.</Th><Th className="text-right">Qtd</Th><Th className="text-right">Valor</Th></tr></thead>
        <tbody>
          {itens.slice(0, 10).map((p, i) => (
            <tr key={`${p.categoria}-${p.plano}-${i}`} className="border-b border-gray-50 dark:border-purple-900/20">
              <Td className="text-gray-500">{p.categoria}</Td>
              <Td className="font-medium">{p.plano}</Td>
              <Td className="text-right tabular-nums">{p.vendas}</Td>
              <Td className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">{p.instaladas}</Td>
              <Td className="text-right tabular-nums">{p.quantidade}</Td>
              <Td className="text-right tabular-nums whitespace-nowrap">{fmtBRL(p.valor)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Painel>
);

export const TabelaJornada = ({ itens }) => (
  <Painel titulo="Jornada e horas extras" descricao="Horas trabalhadas no período x produção" className="xl:col-span-2" testid="dc-jornada">
    {!itens?.length ? <Vazio texto="Nenhuma jornada registrada no período" /> : (
      <div className="overflow-x-auto -mx-2">
        <table className="w-full">
          <thead><tr className="border-b border-gray-100 dark:border-purple-900/40"><Th>Colaborador</Th><Th className="text-right">Dias</Th><Th className="text-right">Horas</Th><Th className="text-right">Média/dia</Th><Th className="text-right">Almoço</Th><Th className="text-right">H. extras</Th><Th className="text-right">Vendas</Th><Th className="text-right">Inst.</Th><Th className="text-right">Inst./h</Th></tr></thead>
          <tbody>
            {itens.map((j) => (
              <tr key={j.user_id} className="border-b border-gray-50 dark:border-purple-900/20">
                <Td className="font-semibold whitespace-nowrap">{j.nome}</Td>
                <Td className="text-right tabular-nums">{j.dias}</Td>
                <Td className="text-right tabular-nums font-bold">{j.horas}h</Td>
                <Td className="text-right tabular-nums">{j.media_dia_horas}h</Td>
                <Td className="text-right tabular-nums text-gray-500">{Math.round(j.almoco_min / 60 * 10) / 10}h</Td>
                <Td className="text-right tabular-nums">{j.horas_extras ? <span className="text-amber-600 font-semibold">{j.horas_extras}h</span> : <span className="text-gray-300">—</span>}</Td>
                <Td className="text-right tabular-nums">{j.vendas}</Td>
                <Td className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">{j.instaladas}</Td>
                <Td className="text-right tabular-nums text-gray-500">{j.horas ? (j.instaladas / j.horas).toFixed(2) : '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Painel>
);
